import { useState } from 'react';
import {
  Badge,
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  NavbarItem,
} from '@accure/ui';
import { EllipsisVertical } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatListInline } from '@/utils/format/list';

import { type Role } from '../entities/role';
import { GetRolesQuery } from '../queries/get-roles';

import { RolesTableSkeletonRow } from './roles-table-skeleton-row';

type RolesTableProps = {
  onEditRole: (role: Role) => void;
  onDeleteRole: (role: Role) => void;
};

const pageSize = 10;

export function RolesTable({ onEditRole, onDeleteRole }: RolesTableProps) {
  const [page, setPage] = useState<number>(1);
  const rolesQuery = GetRolesQuery.useQuery({ page, pageSize });

  function handlePreviousPage() {
    if (typeof rolesQuery.data?.previousPage === 'number') {
      setPage(rolesQuery.data.previousPage);
    }
  }

  function handleNextPage() {
    if (typeof rolesQuery.data?.nextPage === 'number') {
      setPage(rolesQuery.data.nextPage);
    }
  }

  if (rolesQuery.isError) {
    throw rolesQuery.error;
  }

  return (
    <>
      <Table className="w-full table-auto caption-bottom text-sm">
        <TableHead>
          <TableRow className="border-b transition-colors hover:bg-muted/50">
            <TableHeader className="h-10 !px-4 py-2 align-middle font-medium text-muted-foreground">
              Names
            </TableHeader>
            <TableHeader className="h-10 p-2 text-left align-middle font-medium text-muted-foreground">
              Permission
            </TableHeader>
            <TableHeader className="h-10 p-2 text-left align-middle font-medium text-muted-foreground">
              Sites
            </TableHeader>
            <TableHeader className="h-10 p-2 text-left align-middle font-medium text-muted-foreground">
              Users
            </TableHeader>
            <TableHeader className="size-10 px-4 text-left align-middle font-medium text-muted-foreground">
              <span className="sr-only">Actions</span>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rolesQuery.isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <RolesTableSkeletonRow key={index} />
            ))
          ) : rolesQuery.data ? (
            rolesQuery.data.roles.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="p-8 text-center text-muted-foreground"
                >
                  No roles available.
                </TableCell>
              </TableRow>
            ) : (
              rolesQuery.data.roles.map((role) => (
                <TableRow
                  key={role.id}
                  className="cursor-pointer border-b transition-colors hover:bg-muted/50"
                >
                  <TableCell className="!px-4 py-2 align-middle">
                    <div className="font-medium text-gray-900">{role.name}</div>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <Badge variant="outline" className="text-gray-700">
                      {role.permission}
                    </Badge>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <div className="text-gray-600">
                      {role.sites ? (
                        formatListInline(
                          role.sites.map((site) => site.fullFrontendId),
                          'conjunction',
                        )
                      ) : (
                        <i>All sites</i>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <div className="text-gray-600">{role.userCount}</div>
                  </TableCell>
                  <TableCell className="p-2 text-center align-middle">
                    <Dropdown>
                      <DropdownButton
                        as={NavbarItem}
                        className="size-8 border-0 bg-transparent p-0 hover:bg-gray-100"
                      >
                        <EllipsisVertical className="size-4" />
                        <span className="sr-only">Open menu</span>
                      </DropdownButton>
                      <DropdownMenu
                        className="absolute right-0 z-50 min-w-48 rounded border bg-white shadow-lg"
                        anchor="bottom"
                      >
                        <DropdownItem
                          className="transition-colors hover:bg-gray-50"
                          onClick={() => onEditRole(role)}
                        >
                          <DropdownLabel>Edit Role</DropdownLabel>
                        </DropdownItem>
                        <DropdownItem
                          className="text-red-600 transition-colors hover:bg-red-50 disabled:text-gray-400 disabled:hover:bg-transparent"
                          onClick={() => onDeleteRole(role)}
                          disabled={role.userCount > 0}
                          title={
                            role.userCount > 0
                              ? "You can't delete a role if it is assigned to someone"
                              : undefined
                          }
                        >
                          <DropdownLabel>Delete Role</DropdownLabel>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              ))
            )
          ) : null}
        </TableBody>
      </Table>

      {rolesQuery.data &&
      (typeof rolesQuery.data.nextPage === 'number' ||
        typeof rolesQuery.data.previousPage === 'number') ? (
        <nav className="flex items-center justify-between px-4 py-3 sm:px-6">
          <div className="hidden sm:block">
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">
                {Math.max((rolesQuery.data.currentPage - 1) * pageSize + 1, 1)}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(
                  rolesQuery.data.currentPage * pageSize,
                  rolesQuery.data.totalCount,
                )}
              </span>{' '}
              of{' '}
              <span className="font-medium">{rolesQuery.data.totalCount}</span>{' '}
              roles
            </p>
          </div>
          <div className="flex flex-1 justify-between gap-2 sm:justify-end">
            <Button
              variant="ghost"
              disabled={typeof rolesQuery.data.previousPage !== 'number'}
              onClick={handlePreviousPage}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              disabled={typeof rolesQuery.data.nextPage !== 'number'}
              onClick={handleNextPage}
            >
              Next
            </Button>
          </div>
        </nav>
      ) : null}
    </>
  );
}
